import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import DeviceMgr from 'coreClasses/device/DeviceMgr';
import {EEventSource} from 'coreClasses/events/IEvents';
import ProductModel from 'coreClasses/product/ProductModel';
import ViewportMgr from 'coreClasses/view/ViewportMgr';
import ComponentErrorBoundary from 'coreComponents/ComponentErrorBoundary';
import LoaderComponent from 'coreComponents/LoaderComponent';
import NonCriticalBoundary from 'coreComponents/NonCriticalBoundary';
import {
	useClientServerEffect,
	useConfiguration,
	useCustomerMgr,
	useGeolocalizedSizes,
	useLocalesMgr,
	useLocation,
	usePrefetchCmsContent,
	useProduct,
	useRefetchProducts,
	useServerData,
	useSetUI,
	useSSRRequest,
	useSSRResponse,
	useSuspenseCmsContent,
	useUI,
} from 'coreHooks';
import {useCategory, useCategoryActions} from 'coreHooks/categories';
import useBannedProduct from 'coreHooks/useBannedProduct';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import Contentful from 'brand/app/contentful/components/Contentful';
import AddToCartWrapper from 'brandComponents/AddToCartWrapper';
import SizeGuideButton from 'brandComponents/flyouts/SizeGuideFlyout/SubComponents/SizeGuideButton';
import Grid from 'brandComponents/Grid';
import KlarnaMessage from 'brandComponents/KlarnaMessage';
import ShowOrHideOnBreakpoint from 'brandComponents/Responsive/ShowOrHideOnBreakpoint';
import SizeSelectorButton from 'brandComponents/SizeSelectorButton';
import SkeletonPDP from 'brandComponents/SkeletonPDP';
import {MAX_CACHE_AGE} from 'brandConstants';
import {useHandlePageEvents} from 'brandHooks/useHandlePageEvents';

import {useProductDetailContext} from './context/ProductDetailContext';
import ProductBreadcrumbs from './Subcomponents/ProductBreadcrumbs';

const fallback = <LoaderComponent />;
const PageNotFound = lazyComponent(() => import('../../pages/page-not-found'), {fallback});

import {lazyComponent} from 'coreUtils/adapters/lazy';

import ProductCTA from './Subcomponents/ProductCTA/ProductCTA';
import messages from './messages';
import {
	ProductAccordion,
	ProductColorSelection,
	ProductContentAsset,
	ProductDetailBottomBlock,
	ProductDetailDescriptionData,
	ProductDetailFloatingButtons,
	ProductDetailImages,
	ProductDetailZoomCarousel,
} from './Subcomponents';

import styles from './ProductDetail.module.scss';

interface IProductDetailBaseProps {
	pid: string;
	variationGroupId: string;
	isRequiresLoginForRegisteredBannedA?: boolean;
}

const ProductDetailBase = ({
	pid,
	variationGroupId,
	isRequiresLoginForRegisteredBannedA,
}: IProductDetailBaseProps) => {
	const {managerRepository} = useGeneralProvider();
	const res = useSSRResponse();
	const req = useSSRRequest();
	const categoryActions = useCategoryActions();
	const variationGroup: ProductModel = useProduct(variationGroupId);
	const primaryCategory = useCategory(variationGroup.getPrimaryCategoryId());

	const editorialContentOptions = {
		contentType: 'module',
		config: {
			variables: {
				id: variationGroup?.getPdpDesktopEditorialID(),
			},
		},
		enabled: !!variationGroup?.getPdpDesktopEditorialID(),
		collectionKey: 'moduleCollection',
	};

	// Starts the editorial request now: `useSuspenseCmsContent` below only runs once `pdp-base-data` resolved.
	usePrefetchCmsContent(editorialContentOptions);

	const pdpBaseData = useServerData('pdp-base-data', async () => {
		const isBot: boolean = DeviceMgr.isBot(
			req ? req.get('User-Agent') : navigator?.userAgent,
		);
		// Independent requests: run them in parallel.
		const [isProductBannedA, categoryModel] = await Promise.all([
			variationGroup?.isBannedA(),
			categoryActions.getCategoryPromise(variationGroup?.getPrimaryCategoryId()),
		]);

		if (typeof variationGroup?.type === 'string' || !variationGroup?.getProductId()) {
			return {notFound: true, isProductBannedForSSR: false, isRequiresLoginSSR: false};
		}

		if (isBot && isProductBannedA && !isRequiresLoginForRegisteredBannedA) {
			return {notFound: true, isProductBannedForSSR: false, isRequiresLoginSSR: false};
		}

		const currentCountry = managerRepository.getLocalesMgr().getCurrentCountry();
		const isMultiCurrency = currentCountry.isMultiCurrency(EEventSource.PDP);
		const htmlClasses = ['pdp'];

		if (isMultiCurrency) {
			htmlClasses.push('multi-currency');
		}

		managerRepository.getUiMgr().addHtmlCssClasses(htmlClasses);

		res?.setCache?.(MAX_CACHE_AGE);

		if (res?.cacheTagManager) {
			const variants = variationGroup?.getVariants?.();

			res.cacheTagManager.addProductTag();
			res.cacheTagManager.addProductTag(pid);
			variants?.forEach((v) => res.cacheTagManager.addProductTag(v.getProductId()));

			if (primaryCategory) {
				res.cacheTagManager.addCategoryTag(primaryCategory?.getId());
				primaryCategory?.getParentCategoryTreeIds()?.forEach((id) => res.cacheTagManager.addCategoryTag(id));
			}
		}

		const isRequiresLoginSSR = categoryModel?.isRequiresLoginInHierarchy() ?? false;

		return {
			isProductBannedForSSR: isProductBannedA && !isRequiresLoginForRegisteredBannedA,
			isRequiresLoginSSR,
			notFound: false,
		};
		// `ProductDetailApi` does not know whether the product asks to log in: not the same result for the same pid.
	}, [pid, !!isRequiresLoginForRegisteredBannedA]);

	if (pdpBaseData.notFound) {
		return <PageNotFound />;
	}

	return (
		<ProductDetailBaseInner
			pid={pid}
			variationGroupId={variationGroupId}
			variationGroup={variationGroup}
			isProductBannedForSSR={pdpBaseData.isProductBannedForSSR}
			editorialContentOptions={editorialContentOptions}
			isRequiresLoginSSR={pdpBaseData.isRequiresLoginSSR}
			isRequiresLoginForRegisteredBannedA={isRequiresLoginForRegisteredBannedA}
		/>
	);
};

type CmsContentOptions = Parameters<typeof useSuspenseCmsContent>[0];

/**
 * Editorial module below the product: not needed to render the PDP (see `NonCriticalBoundary`).
 */
const ProductEditorial = ({options}: {options: CmsContentOptions}) => {
	const {data} = useSuspenseCmsContent(options);

	if (!data) {
		return null;
	}

	return (
		<Grid.Row>
			<Contentful initialData={data} />
		</Grid.Row>
	);
};

interface IProductDetailBaseInnerProps {
	pid: string;
	variationGroupId: string;
	isProductBannedForSSR: boolean;
	editorialContentOptions: CmsContentOptions;
	isRequiresLoginSSR: boolean;
	isRequiresLoginForRegisteredBannedA?: boolean;
	variationGroup: ProductModel;
}

const ProductDetailBaseInner = ({
	pid,
	variationGroupId,
	isProductBannedForSSR,
	editorialContentOptions,
	isRequiresLoginSSR,
	isRequiresLoginForRegisteredBannedA,
	variationGroup,
}: IProductDetailBaseInnerProps) => {
	const {managerRepository, getRef} = useGeneralProvider();
	const intl = useIntl();
	const customer = useCustomerMgr().getCurrentCustomer();
	const {isReady: isCustomerReady, isRegistered} = customer;
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const queryPid = params.get('pid');
	const setUI = useSetUI();
	const sizeSelectorSelectedPID = useUI('sizeSelectorSelectedPID');
	const isServerUI = useUI('isServer');
	const localesMgr = useLocalesMgr();
	const primaryCategory = useCategory(variationGroup.getPrimaryCategoryId());
	const isRequiresLoginEnabled =
		(isRequiresLoginSSR && isServerUI) || (primaryCategory?.isRequiresLoginInHierarchy() && isCustomerReady && !isRegistered);
	const parentCategory = useCategory(primaryCategory?.getParentCategoryId());
	const imagesWrapperRef = useRef<HTMLDivElement>(null);
	const detailsRef = useRef<HTMLDivElement>(null);
	const [selectedPID, setSelectedPID] = useState<string>(pid);
	const querySelectedVariant = useProduct(queryPid);
	const selectedProduct = useProduct(selectedPID);
	const geolocalizedSizes = useGeolocalizedSizes(selectedProduct);
	const sizeSelectorLabel = selectedProduct.isVariant()
		? `${intl.formatMessage(messages['sizeSelectorSelected'])}: ${geolocalizedSizes[selectedPID]}`
		: intl.formatMessage(messages['sizeSelector']);
	const scrollContainer = getRef('appMain');
	const currentCountry = localesMgr.getCurrentCountry();
	const isMultiCurrency = currentCountry.isMultiCurrency(EEventSource.PDP);
	const productDetailRef = useRef(null);
	const productDetailDescriptionRef = useRef(null);
	const [isScrolled, setIsScrolled] = useState(false);
	const {isBreadCrumbsShow} = useProductDetailContext();
	const fit = variationGroup.getFitInformation();
	const {isBannedA} = useBannedProduct(variationGroup);
	const resetMobileScroll = useCallback(() => {
		if (ViewportMgr.isMobile() && scrollContainer.current) {
			scrollContainer.current.scrollTo({
				top: 0,
				left: 0,
				behavior: 'instant',
			});

			managerRepository.getUiMgr().removeMainContainerCssClasses(['unlocked']);
		}
	}, []);

	const productCategory = useCategory(variationGroup.getCategoryId());
	const cmsSlotsId = variationGroup.getCustomSlotsId();
	const enableEnhancedSearchProductResult = useConfiguration('sitePreferences.enableEnhancedSearchProductResult') || false;
	const variationGroupsToRefetch = useMemo(() => {
		if (enableEnhancedSearchProductResult) {
			return [variationGroup];
		} else {
			return [];
		}
	}, [variationGroup]);

	useRefetchProducts(variationGroupsToRefetch);

	const dataLayerObj = useMemo(
		() => ({
			source: EEventSource.PDP,
			eventSourceCategoryId: productCategory?.getId(),
			eventSourceCategoryName: productCategory?.getName(),
			productData: {
				product: variationGroup,
				productCategoriesIds: productCategory?.getParentCategoryTreeIds(),
				attributes: {
					dimension9: variationGroup.getSeason(),
					dimension10: variationGroup.getPriceModel().isSalesPriceBookApplied() ? 'MD' : 'FP',
				},
			},
		}),
		[productCategory, variationGroup],
	);

	// Not in the `pdp-base-data` queryFn: it does not run again for a cached product.
	useClientServerEffect(() => {
		if (isRequiresLoginEnabled) {
			managerRepository.getUiMgr().addHtmlCssClasses(['requires-login-lock-scroll']);
		}
	}, [isRequiresLoginEnabled]);

	useEffect(() => {
		setUI.setUI({variationGroupId: variationGroupId, requiresLoginFeature: {enabled: isRequiresLoginEnabled}});
		!isRequiresLoginEnabled && setUI.removeHtmlCssClasses(['requires-login-lock-scroll']);

		return () => {
			setUI.setUI({variationGroupId: null, requiresLoginFeature: {enabled: false}});
			setUI.removeHtmlCssClasses(['requires-login-lock-scroll']);
		};
	}, [variationGroupId, isRequiresLoginEnabled]);

	useEffect(() => {
		resetMobileScroll();

		return () => {
			resetMobileScroll();
			managerRepository.getUiMgr().removeHtmlCssClasses(['pdp', 'multi-currency']);
		};
	}, []);

	useClientServerEffect(() => {
		if (isMultiCurrency) {
			managerRepository.getUiMgr().addHtmlCssClasses(['pdp', 'multi-currency']);
		} else {
			managerRepository.getUiMgr().addHtmlCssClasses(['pdp']);
			managerRepository.getUiMgr().removeHtmlCssClasses(['multi-currency']);
		}
	}, [isMultiCurrency]);

	useEffect(() => {
		if (!location.currentRoute.startsWith('Product-Show')) {
			resetMobileScroll();
			managerRepository.getUiMgr().removeHtmlCssClasses(['multi-currency', 'pdp']);
		}
	}, [location.currentRoute]);

	useEffect(() => {
		if (pid !== selectedPID) {
			setSelectedPID(pid);
		}
	}, [pid]);

	useEffect(() => {
		if (queryPid && querySelectedVariant && querySelectedVariant.isReady() && querySelectedVariant.isOrderable()) {
			setSelectedPID(queryPid);
		}
	}, [querySelectedVariant, queryPid, pid]);

	useEffect(() => {
		if (!selectedProduct.isVariant()) {
			const variants = selectedProduct.getVariants();

			if (variants?.length === 1 && variants[0].isOrderable()) {
				setSelectedPID(variants[0].getProductId());
			}
		}
	}, [selectedProduct]);

	useEffect(resetMobileScroll, [variationGroup.getSlugUrl()]);

	useEffect(() => {
		if (sizeSelectorSelectedPID && !!variationGroup.getVariant(sizeSelectorSelectedPID)) {
			setSelectedPID(sizeSelectorSelectedPID);
		}
	}, [sizeSelectorSelectedPID]);

	useEffect(() => {
		if (productDetailRef.current) {
			const observer = new IntersectionObserver(
				([e]) => {
					const {bottom} = e.boundingClientRect;
					const viewportHeight = window.visualViewport?.height ?? window.innerHeight;

					if (e.isIntersecting) {
						setIsScrolled(true);
					} else if (bottom > viewportHeight - 70) {
						setIsScrolled(false);
					}
				},
				{
					rootMargin: '0px 0px -70px 0px',
					threshold: 1,
				},
			);

			observer.observe(productDetailRef.current);

			return () => {
				observer.disconnect();
			};
		}
	}, [productDetailRef]);

	useHandlePageEvents(
		{
			pageType: EEventSource.PDP,
			pageSpecificData: {
				productName: variationGroup.getName(),
				productSku: variationGroup.getProductId(),
				variantID: variationGroup.getProductId(),
				productMasterID: variationGroup.getProductId(),
				productPrice: variationGroup.getPriceModel().getMinPrice(),
				productCategory: parentCategory?.getId(),
				productSubCategory: primaryCategory?.getId(),
				productType: primaryCategory?.getName(),
			},
			dataLayerObj,
			isPageInitialized: !!variationGroup.getPrice().value,
		},
		[
			variationGroup.slugUrl,
			variationGroup.getPriceModel()?.getMinPrice()?.value,
			variationGroup.getPriceModel()?.getMaxPrice()?.value,
		],
	);

	if (isProductBannedForSSR) {
		return <SkeletonPDP />;
	}

	if (isBannedA && !isRequiresLoginForRegisteredBannedA) {
		return <PageNotFound />;
	}

	return (
		<div
			className={cn(styles['product-detail'], {
				[styles['product_detail__mobile-breadcrumbs--open']]: isBreadCrumbsShow,
			})}
			ref={detailsRef}
		>
			<div
				className={cn(styles['product-detail-overlay'], {
					[styles['product-detail-overlay__visible']]: isRequiresLoginEnabled,
				})}
			/>
			<ProductDetailZoomCarousel product={variationGroup} />
			<Grid fluid>
				<Grid.Row>
					<Grid.Column lg={9} classNames={styles['product-detail__img-wrapper']} ref={imagesWrapperRef}>
						<ProductDetailImages product={variationGroup} />
					</Grid.Column>
					<Grid.Column ref={productDetailDescriptionRef} lg={3} classNames={styles['product-detail__description__wrapper']}>
						<div ref={productDetailRef} className={styles['product-detail__description']}>
							<section className="product-detail__description__section">
								<div className={cn(styles['product-detail__description__heading'], 'product-detail__description__heading')}>
									<ComponentErrorBoundary>
										<ProductDetailDescriptionData
											variationGroup={variationGroup}
											productId={selectedProduct.getProductId()}
											selectedProduct={selectedProduct}
											isRequiresLoginForRegisteredBannedA={isRequiresLoginForRegisteredBannedA}
										/>
									</ComponentErrorBoundary>
									<KlarnaMessage amount={selectedProduct.priceModel.getSales()?.value} dataIdentifier="pdp" />
								</div>
								<ComponentErrorBoundary>
									<ProductColorSelection product={variationGroup} />
								</ComponentErrorBoundary>
								<div className={styles['product-detail__description__size-guide-wrapper']}>
									{fit && <span className={styles['product-detail__description__fit']}>{fit}</span>}
									<ComponentErrorBoundary>
										<SizeGuideButton product={variationGroup} renderSize={true} renderItem={false} closeOnBack={true} />
									</ComponentErrorBoundary>
								</div>
								<ComponentErrorBoundary>
									{variationGroup.variants?.length > 1 && (
										<SizeSelectorButton
											variationGroupId={variationGroupId}
											variantId={selectedProduct.isVariant() && selectedProduct.getProductId()}
											label={sizeSelectorLabel}
											variant="primary-white"
										/>
									)}
									<ShowOrHideOnBreakpoint query=">=small">
										<AddToCartWrapper
											source={EEventSource.PDP}
											product={selectedProduct}
											variationGroupId={variationGroupId}
											variant="primary"
											labels={{
												addToCart: intl.formatMessage(
													messages[selectedProduct.isOrderable() ? 'addToCartLabel' : 'notifyMeLabel'],
												),
												preOrderNow: intl.formatMessage(messages.preOrderNow),
											}}
											overrideButtonsConfigurations={{
												expressPayment: true,
											}}
										/>
									</ShowOrHideOnBreakpoint>
								</ComponentErrorBoundary>
							</section>
							<ShowOrHideOnBreakpoint query=">small">
								{cmsSlotsId && <ProductContentAsset id={cmsSlotsId} />}
							</ShowOrHideOnBreakpoint>
							<div
								className={cn(styles['product-detail__additional-links'], styles['product-detail__additional-links--dsk'])}
							>
								<ComponentErrorBoundary>
									<ProductAccordion
										product={variationGroup}
										contentfulIDs={variationGroup.getAdditionalLinks()}
										hTag="h3"
									/>
								</ComponentErrorBoundary>

								<div className={styles['product-detail__ctas-wrapper']}>
									<ProductCTA />
								</div>
							</div>
						</div>
						<ComponentErrorBoundary>
							<ProductDetailFloatingButtons
								isScrolled={isScrolled}
								isMultiCurrency={isMultiCurrency}
								product={selectedProduct}
								variationGroup={variationGroup}
							/>
							<ShowOrHideOnBreakpoint query="<=small">
								{cmsSlotsId && <ProductContentAsset id={cmsSlotsId} />}
							</ShowOrHideOnBreakpoint>
							<div
								className={cn(styles['product-detail__additional-links'], styles['product-detail__additional-links--mob'])}
							>
								<ProductAccordion product={variationGroup} contentfulIDs={variationGroup.getAdditionalLinks()} />
								<div className={styles['product-detail__ctas-wrapper']}>
									<ProductCTA />
								</div>
							</div>
						</ComponentErrorBoundary>
					</Grid.Column>
				</Grid.Row>
				<NonCriticalBoundary>
					<ProductEditorial options={editorialContentOptions} />
				</NonCriticalBoundary>
				<Grid.Row classNames={styles['product-detail__bottom']}>
					<Grid.Column>
						<NonCriticalBoundary>
							<ProductDetailBottomBlock product={variationGroup} />
						</NonCriticalBoundary>
					</Grid.Column>
				</Grid.Row>
				<Grid.Row classNames={styles['product-detail__bradcrumbs']}>
					<ProductBreadcrumbs selectedProduct={variationGroup} />
				</Grid.Row>
			</Grid>
		</div>
	);
};

ProductDetailBase.displayName = 'ProductDetailBase';

export default ProductDetailBase;
